import React, { useState } from 'react';
import { Radio, Empty } from '@maxtropy/components';
import { Progress, RadioChangeEvent, Spin } from 'antd';
import { useRequest } from 'ahooks';
import {
  apiV2HighEnergyDeviceAnalysisRankingSummaryPost,
  V2HighEnergyDeviceAnalysisRankingSummaryPostResponse,
  V2HighEnergyDeviceAnalysisRankingSummaryPostRequest,
} from '@maxtropy/device-customer-apis-v2';
import { keepTwoOrLine, keepTwoAndWanOrLine } from '@/utils/utils';
import styles from './index.module.scss';

// 排名类型
export enum RankType {
  UNIT_POWER = 'unitPower', // 单位时间用电情况排名
  USAGE_RATE = 'usageRate', // 设备使用率排名
}

export interface DeviceRankingListProps {
  timeParams: {
    startTime: number;
    endTime: number;
    timeResolution: string;
  };
  deviceTypeId?: number | string;
}

const DeviceRankingList: React.FC<DeviceRankingListProps> = props => {
  const { timeParams, deviceTypeId } = props;
  const [rankType, setRankType] = useState<RankType>(RankType.UNIT_POWER);

  // 获取排名数据
  const { data: rankingData, loading } = useRequest(
    () => {
      if (!timeParams.startTime || !timeParams.endTime) {
        return Promise.resolve({ rankingList: [] } as V2HighEnergyDeviceAnalysisRankingSummaryPostResponse);
      }
      return apiV2HighEnergyDeviceAnalysisRankingSummaryPost({
        startTime: timeParams.startTime,
        endTime: timeParams.endTime,
        timeResolution: timeParams.timeResolution,
        type: rankType,
        deviceTypeId, // 添加 deviceTypeId 参数
      } as V2HighEnergyDeviceAnalysisRankingSummaryPostRequest);
    },
    {
      refreshDeps: [timeParams.startTime, timeParams.endTime, timeParams.timeResolution, rankType, deviceTypeId], // 添加 deviceTypeId 到依赖
    }
  );

  const getRankClassName = (rank: number) => {
    switch (rank) {
      case 1:
        return styles.rankFirst;
      case 2:
        return styles.rankSecond;
      case 3:
        return styles.rankThird;
      default:
        return styles.rankOther;
    }
  };

  // 对API返回的数据进行格式化，并添加排名
  const formattedData = (rankingData?.rankingList || []).map((item, index) => ({
    ...item,
    rank: index + 1,
  }));

  return (
    <div className={styles.unifiedRankList}>
      <div className={styles.radioGroup}>
        <Radio.Group
          onChange={(e: RadioChangeEvent) => setRankType(e.target.value)}
          value={rankType}
          buttonStyle="solid"
        >
          <Radio.Button value={RankType.UNIT_POWER}>单位时间用电情况排名</Radio.Button>
          <Radio.Button value={RankType.USAGE_RATE}>设备使用率排名</Radio.Button>
        </Radio.Group>
      </div>
      <div className={styles.listWrapper}>
        <div className={styles.list}>
          {loading ? (
            <div className={styles.loadingContainer}>
              <Spin />
            </div>
          ) : rankingData?.rankingList && rankingData.rankingList.length > 0 ? (
            formattedData.map((item, index) => {
              // 根据当前选择的排名类型来决定显示的内容
              const isUnitPowerType = rankType === RankType.UNIT_POWER;

              return (
                <div className={styles.card} key={item.deviceId || index}>
                  <div className={styles.cardHeader}>
                    <div className={`${styles.rankNum} ${getRankClassName(item.rank!)}`}>{item.rank}</div>
                    <span className={styles.deviceName}>{item.deviceName || '--'}</span>
                  </div>
                  <div className={styles.divider} />
                  <div className={styles.cardContent}>
                    {isUnitPowerType ? (
                      // 单位时间用电情况排名 - 显示为数值和单位
                      <>
                        <div className={styles.row}>
                          <span className={styles.label}>单位时间用电：</span>
                          <span className={styles.value} style={{ color: '#52E7FF' }}>
                            {keepTwoOrLine(item.rankingValue)}kWh/h
                          </span>
                        </div>
                        <div className={styles.row}>
                          <span className={styles.label}>额定功率：</span>
                          <span className={styles.value} style={{ color: '#FFCB47' }}>
                            {keepTwoOrLine(item.ratedPower)}kW
                          </span>
                        </div>
                      </>
                    ) : (
                      // 设备使用率排名 - 显示为进度条和百分比
                      <>
                        <div className={styles.row}>
                          <span className={styles.label}>使用率：</span>
                          <Progress
                            className={styles.progress}
                            percent={Math.round(item.rankingValue || 0)}
                            strokeColor="#16DD8E"
                            trailColor="rgba(255,255,255,0.2)"
                            size="small"
                            showInfo={false}
                          />
                          <span className={styles.usageText}>{keepTwoOrLine(item.rankingValue)}%</span>
                        </div>
                        <div className={styles.row}>
                          <span className={styles.label}>用电：</span>
                          <span className={styles.value}>{keepTwoAndWanOrLine(item.energyConsumption)}kWh</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div className={styles.emptyContainer}>
              <Empty />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeviceRankingList;
